import React, { useState } from 'react';
import RelationshipMap from './RelationshipMap';
import OutputGraph from './OutputGraph';
import TransformationMaturityGraph from '../../pages/dashboard_pages/TransformationMaturityGraph';
import { CustomerResponse } from '../../types';
import CustomerOverview from '../../components/customer/CustomerOverview';
import TransformationRoadmapChart from '../../components/common/TransformationRoadmapChart';
import { createRoadmapDataFromProjects } from '../../utils/transform-project-to-tasks-utils';
import GoalsTab from './GoalsTab';
import ProjectsTab from './ProjectsTab';
import TechnologyAlignmentTab from './TechnologyAlignmentTab';
import ArtOfPossibleTab from './ArtOfPossibleTab';
import ApplicationsTab from './ApplicationsTab';
import Frameworks from '../Frameworks';
import { HelpButton } from '../../components/common/HelpButton';
import { InfoModal } from '../../components/common/InfoModal';
import { DashboardSectionId, getDashboardSectionAnchorId } from './dashboardSections';

interface Props {
    customerData: CustomerResponse | undefined;
    isLoading: boolean;
    error: string | null;
    onNavigateToGoals?: () => void;
}

interface DashboardSectionWrapperProps {
    sectionId: DashboardSectionId;
    children: React.ReactNode;
}

const DashboardSectionWrapper: React.FC<DashboardSectionWrapperProps> = ({ sectionId, children }) => (
    <section
        id={getDashboardSectionAnchorId(sectionId)}
        data-dashboard-section={sectionId}
        className="scroll-mt-6 overflow-hidden rounded-[30px] border border-slate-200/80 bg-gradient-to-br from-white via-white to-slate-50/80 shadow-[0_18px_40px_rgba(15,42,79,0.06)] ring-1 ring-white/70"
    >
        <div className="border-t border-white/80">
            {children}
        </div>
    </section>
);

const technologyMapDescription = "This map shows how business goals connect to the projects and technology decisions needed to achieve them. Each path traces a relationship from a Goal to a Project, then to a Practice, Sub-Practice, and specific Technology, making it easier to see how strategic priorities depend on delivery work and technical capabilities.";

const CustomerDashboardTab: React.FC<Props> = ({ customerData, isLoading, error, onNavigateToGoals }) => {
    const [technologyMapView, setTechnologyMapView] = useState<'orig' | 'new'>('new');
    const [showTechnologyMapInfo, setShowTechnologyMapInfo] = useState(false);

    // Create dynamic roadmap data from customer projects
    const roadmapData = customerData?.top_10_projects?.top_projects 
        ? createRoadmapDataFromProjects(customerData.top_10_projects.top_projects)
        : undefined;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen w-full">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]" />
            </div>
        );
    }

    if (!customerData?.customer_details) {
        return (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
                <h2 className="text-lg font-semibold text-[#0F2A4F]">
                    {error ? 'Unable to load customer profile' : 'Loading customer profile'}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                    {error || 'The profile details are still being fetched.'}
                </p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-8">
                <DashboardSectionWrapper sectionId="customer-dashboard">
                    <CustomerOverview
                        customerDetails={customerData}
                        onNavigateToGoals={onNavigateToGoals}
                        showSummaryCards={false}
                    />
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="key_goals">
                    <GoalsTab goalsData={customerData?.goals} loading={isLoading} error={error} />
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="projects">
                    <ProjectsTab projects={customerData?.top_10_projects?.top_projects} />
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="relationship-map">
                    <div className="space-y-4 p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <h2 className="text-2xl font-semibold text-[#0F2A4F] flex flex-wrap items-center gap-3">
                                <span>Goal → Project → Practice/SubPractice/Technology Map</span>
                                <HelpButton onClick={() => setShowTechnologyMapInfo(true)} className="shrink-0" />
                            </h2>
                            <div className="inline-flex rounded-md border border-gray-200 bg-gray-100 p-1 shadow-sm" aria-label="Technology map version">
                                <button
                                    type="button"
                                    onClick={() => setTechnologyMapView('orig')}
                                    className={`h-9 rounded px-4 text-sm font-medium transition-colors ${
                                        technologyMapView === 'orig'
                                            ? 'bg-[#0F2A4F] text-white shadow-sm'
                                            : 'text-[#0F2A4F] hover:bg-white'
                                    }`}
                                    aria-pressed={technologyMapView === 'orig'}
                                >
                                    Orig
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTechnologyMapView('new')}
                                    className={`h-9 rounded px-4 text-sm font-medium transition-colors ${
                                        technologyMapView === 'new'
                                            ? 'bg-[#0F2A4F] text-white shadow-sm'
                                            : 'text-[#0F2A4F] hover:bg-white'
                                    }`}
                                    aria-pressed={technologyMapView === 'new'}
                                >
                                    New
                                </button>
                            </div>
                        </div>

                        {technologyMapView === 'orig' ? (
                            <OutputGraph
                                projects={customerData?.top_10_projects?.top_projects}
                                customerName={customerData?.customer_details?.name || 'Customer'}
                                hideHeader
                                hideDescription
                                controlsVariant="mapButtons"
                            />
                        ) : (
                            <RelationshipMap customerData={customerData} />
                        )}
                    </div>
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="technology_alignment">
                    <TechnologyAlignmentTab
                        keyTechnology={customerData?.technology?.key_technology}
                        strategicVendors={customerData?.technology?.strategic_vendors}
                    />
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="art-of-the-possible">
                    <ArtOfPossibleTab aops={{ aops: customerData?.art_of_possible?.aops || [] }} />
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="transformation-graph">
                    <div className="space-y-6 p-4 md:p-5">
                        <div className="overflow-x-auto">
                            <TransformationMaturityGraph customerId={customerData?.customer_id} />
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_14px_30px_rgba(15,42,79,0.06)]">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Transformation Project Roadmap</h3>
                            <div className="overflow-x-auto">
                                <TransformationRoadmapChart
                                    width={1200}
                                    height={500}
                                    data={roadmapData}
                                    showTooltips={true}
                                    showLegends={false}
                                    interactive={true}
                                />
                            </div>
                        </div>
                    </div>
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="business_frameworks">
                    <Frameworks dashboard />
                </DashboardSectionWrapper>

                <DashboardSectionWrapper sectionId="critical_application">
                    <ApplicationsTab criticalApps={customerData?.critical_apps?.critical_apps_list} />
                </DashboardSectionWrapper>
            </div>
            <InfoModal
                isOpen={showTechnologyMapInfo}
                onClose={() => setShowTechnologyMapInfo(false)}
                title="Technology Map"
                message={technologyMapDescription}
            />
        </>
    );
};

export default CustomerDashboardTab;
